import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from 'react';
import { ToastViewport } from '@/components/ui/ToastViewport';

export type ToastVariant = 'success' | 'error' | 'info';

export type ToastItem = {
  id: string;
  message: string;
  variant: ToastVariant;
  createdAt: number;
};

type ToastContextValue = {
  toasts: ToastItem[];
  push: (id: string, message: string, variant?: ToastVariant) => void;
  dismiss: (id: string) => void;
  success: (message: string, id?: string) => void;
  error: (message: string, id?: string) => void;
  info: (message: string, id?: string) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_DURATION_MS = 4000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((id: string, message: string, variant: ToastVariant = 'info') => {
    setToasts((list) => {
      const without = list.filter((t) => t.id !== id);
      return [...without, { id, message, variant, createdAt: Date.now() }];
    });
  }, []);

  const success = useCallback(
    (message: string, id = `toast-${Date.now()}`) => push(id, message, 'success'),
    [push]
  );

  const error = useCallback(
    (message: string, id = `toast-${Date.now()}`) => push(id, message, 'error'),
    [push]
  );

  const info = useCallback(
    (message: string, id = `toast-${Date.now()}`) => push(id, message, 'info'),
    [push]
  );

  useEffect(() => {
    if (!toasts.length) return;
    const timers = toasts.map((t) =>
      window.setTimeout(() => dismiss(t.id), DEFAULT_DURATION_MS)
    );
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [toasts, dismiss]);

  const value = useMemo(
    () => ({ toasts, push, dismiss, success, error, info }),
    [toasts, push, dismiss, success, error, info]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
